import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { formatCreatedAtDate, formatNumberWithComma } from '../../utils/helpers';

// Helper function to get branch name
const getBranchName = (branchId, branchNamesData) => {
  if (!branchNamesData || !Array.isArray(branchNamesData)) return 'Unknown';
  const branch = branchNamesData.find(b => parseInt(b.id) === parseInt(branchId));
  return branch ? `${branch.name}` : 'Unknown';
};

// Helper function to get UOM name
const getUOMName = (uomId, uomData) => {
  if (!uomData || !Array.isArray(uomData)) return 'Unknown';
  const uom = uomData.find(u => parseInt(u.id) === parseInt(uomId));
  return uom ? uom.short_name : 'Unknown';
};

// Helper function to get status name
const getStatusName = (status) => {
  return status === 1 ? 'Active' : 'Inactive';
};

// Helper function to get ingredient level
const getIngredientLevel = (balance) => {
  if (balance <= 0) return 'Out of Stock';
  if (balance <= 10) return 'Low';
  if (balance <= 50) return 'Normal';
  return 'High';
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
  if (!text) return [''];
  const words = String(text).split(' ');
  const lines = [];
  let currentLine = words[0] || '';

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

// Manual table drawing function for PDF with selected columns only
const drawIngredientsTable = (doc, data, startY) => {
  const pageWidth = doc.internal.pageSize.width;
  const margin = 14;
  const rowHeight = 12;

  // Column widths for selected columns: Date, Full Name, Email, Phone, Nationality, Branch
  // For ingredients, we'll use: Date, Item Name, UOM, Quantity, Balance, Status, Branch
  const colWidths = [20, 40, 20, 20, 20, 20, 30]; // Adjusted for 7 columns

  // Check if we need a new page
  let currentY = startY;
  const neededHeight = (data.length + 1) * rowHeight;
  if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
    doc.addPage('landscape');
    currentY = margin;
  }

  // Draw table header
  doc.setFillColor(41, 128, 185); // Blue background
  doc.setTextColor(255, 255, 255); // White text
  doc.setFont(undefined, 'bold');

  const headers = ['Date', 'Item Name', 'UOM', 'Quantity', 'Balance', 'Status', 'Branch'];
  let xPos = margin;

  // Draw header background
  doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

  // Draw header text
  headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
  });

  // Draw header underline
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

  currentY += rowHeight;

  // Draw table rows
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);

  data.forEach((row, rowIndex) => {
    // Calculate needed height for this row
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
      const text = String(cell);
      const maxWidth = colWidths[cellIndex] - 4;
      const lines = splitTextIntoLines(doc, text, maxWidth);
      maxLines = Math.max(maxLines, lines.length);
      return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break
    if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
      doc.addPage('landscape');
      currentY = margin;
      
      // Redraw header on new page
      doc.setFillColor(41, 128, 185);
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      
      doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
      
      xPos = margin;
      headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
      });
      
      doc.setDrawColor(255, 255, 255);
      doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
      
      currentY += rowHeight;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
      doc.setFillColor(248, 248, 248);
    } else {
      doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
      xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
      
      lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
      });
    });
    
    // Add border between rows
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    doc.line(margin, currentY + actualRowHeight, pageWidth - margin, currentY + actualRowHeight);
    
    currentY += actualRowHeight;
  });

  return currentY;
};

// Excel export for Ingredients with all attributes
export const exportToExcel = (ingredients, dataSources = {}) => {
  try {
    const { branchNamesFilterData, unitesNames } = dataSources;
    
    // Prepare data for Excel with all attributes
    const excelData = ingredients.map((ingredient, index) => {
      const branchName = getBranchName(ingredient.branch_id, branchNamesFilterData);
      const uomName = getUOMName(ingredient.uom, unitesNames);
      const ingredientLevel = getIngredientLevel(ingredient.balance);
      
      return {
        'No.': index + 1,
        'Item Name': ingredient.name || '',
        'Quantity': ingredient.quantity || 0,
        'Balance': ingredient.balance || 0,
        'UOM': uomName,
        'Ingredient Level': ingredientLevel,
        'Status': getStatusName(ingredient.status),
        'Branch': branchName,
        'Ingredient Date': formatDate(ingredient.stock_date),
        'Description': ingredient.description || '',
        'Created Date': formatCreatedAtDate(ingredient.created_at),
        'Updated Date': formatDate(ingredient.updated_at)
      };
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
      Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
          widths[idx] = length;
        }
      });
      return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ingredients');

    // Generate Excel file and download
    const fileName = `ingredients_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Ingredients Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
  }
};

// PDF export for Ingredients with selected columns only (landscape mode)
export const exportToPDF = (ingredients, dataSources = {}, filters = {}) => {
  try {
    const { branchNamesFilterData, unitesNames } = dataSources;
    
    const doc = new jsPDF('landscape');
    
    // Set document properties
    doc.setProperties({
      title: 'Ingredients Report',
      subject: 'Ingredients Export',
      author: 'Food Ingredient Management',
      keywords: 'ingredients, inventory, food, export, report',
      creator: 'Food Ingredient Management System'
    });
    
    // Title section
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('INGREDIENTS INVENTORY REPORT', doc.internal.pageSize.width / 2, 25, { align: 'center' });
    
    // Generation details
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    let currentY = 35;
    
    // Report info in two columns
    const leftColumnX = 14;
    const rightColumnX = doc.internal.pageSize.width / 2 + 20;
    
    doc.text(`Report Generated: ${new Date().toLocaleString()}`, leftColumnX, currentY);
    doc.text(`Total Items: ${ingredients.length}`, rightColumnX, currentY);
    currentY += 8;
    
    // Calculate totals
    if (ingredients.length > 0) {
      const totalQuantity = ingredients.reduce((sum, ingredient) => sum + (parseFloat(ingredient.quantity) || 0), 0);
      const totalBalance = ingredients.reduce((sum, ingredient) => sum + (parseFloat(ingredient.balance) || 0), 0);
      
      // Count by ingredient level
      const ingredientLevels = {
        'Out of Stock': 0,
        'Low': 0,
        'Normal': 0,
        'High': 0
      };
      
      ingredients.forEach(ingredient => {
        const level = getIngredientLevel(ingredient.balance);
        ingredientLevels[level]++;
      });
      
      doc.text(`Total Quantity: ${formatNumberWithComma(totalQuantity)}`, leftColumnX, currentY);
      doc.text(`Out of Stock: ${ingredientLevels['Out of Stock']} items`, rightColumnX, currentY);
      currentY += 8;
      doc.text(`Current Balance: ${formatNumberWithComma(totalBalance)}`, leftColumnX, currentY);
      doc.text(`Low Stock: ${ingredientLevels['Low']} items`, rightColumnX, currentY);
      currentY += 8;
    }
    
    // Filters applied
    if (filters.stock_level || filters.status || filters.dateFrom || filters.dateTo) {
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text('Applied Filters:', leftColumnX, currentY);
      currentY += 6;
      
      let filterLines = [];
      if (filters.stock_level) filterLines.push(`• Stock Level: ${filters.stock_level}`);
      if (filters.status) filterLines.push(`• Status: ${getStatusName(filters.status)}`);
      if (filters.dateFrom) filterLines.push(`• From date: ${filters.dateFrom}`);
      if (filters.dateTo) filterLines.push(`• To date: ${filters.dateTo}`);
      
      // Display filters in two columns if many
      const midPoint = Math.ceil(filterLines.length / 2);
      const leftFilters = filterLines.slice(0, midPoint);
      const rightFilters = filterLines.slice(midPoint);
      
      const maxLines = Math.max(leftFilters.length, rightFilters.length);
      
      for (let i = 0; i < maxLines; i++) {
        if (leftFilters[i]) {
          doc.text(leftFilters[i], leftColumnX + 5, currentY);
        }
        if (rightFilters[i]) {
          doc.text(rightFilters[i], rightColumnX, currentY);
        }
        currentY += 6;
      }
      currentY += 6;
    }
    
    // Prepare table data with selected columns: Date, Item Name, UOM, Quantity, Balance, Status, Branch
    const tableData = ingredients.map((ingredient) => {
      const uomName = getUOMName(ingredient.uom, unitesNames);
      const branchName = getBranchName(ingredient.branch_id, branchNamesFilterData);
      
      return [
        formatDate(ingredient.stock_date),
        ingredient.name || '',
        uomName,
        formatNumberWithComma(ingredient.quantity || 0),
        formatNumberWithComma(ingredient.balance || 0),
        getStatusName(ingredient.status),
        branchName
      ];
    });
    
    // Draw the table
    currentY = drawIngredientsTable(doc, tableData, currentY + 10);
    
    // Add footer with page number
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
      doc.text(
        `Generated on: ${new Date().toLocaleDateString()}`,
        doc.internal.pageSize.width - 14,
        doc.internal.pageSize.height - 10,
        { align: 'right' }
      );
    }
    
    const fileName = `ingredients_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Ingredients PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
  }
};

// CSV export for Ingredients with all attributes
export const exportToCSV = (ingredients, dataSources = {}) => {
  try {
    const { branchNamesFilterData, unitesNames } = dataSources;
    
    // CSV headers with all attributes
    const headers = [
      'Item Name',
      'Quantity',
      'Balance',
      'UOM',
      'Ingredient Level',
      'Status',
      'Branch',
      'Ingredient Date',
      'Description',
      'Created Date',
      'Updated Date'
    ];
    
    // Prepare CSV data with all attributes
    const csvData = ingredients.map(ingredient => {
      const branchName = getBranchName(ingredient.branch_id, branchNamesFilterData);
      const uomName = getUOMName(ingredient.uom, unitesNames);
      const ingredientLevel = getIngredientLevel(ingredient.balance);
      
      return [
        `"${(ingredient.name || '').replace(/"/g, '""')}"`,
        ingredient.quantity || 0,
        ingredient.balance || 0,
        `"${uomName.replace(/"/g, '""')}"`,
        `"${ingredientLevel.replace(/"/g, '""')}"`,
        getStatusName(ingredient.status),
        `"${branchName.replace(/"/g, '""')}"`,
        `"${formatDate(ingredient.stock_date).replace(/"/g, '""')}"`,
        `"${(ingredient.description || '').replace(/"/g, '""')}"`,
        `"${formatCreatedAtDate(ingredient.created_at).replace(/"/g, '""')}"`,
        `"${formatDate(ingredient.updated_at).replace(/"/g, '""')}"`
      ];
    });

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ingredients_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Ingredients CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
  }
};

